<template>
  <div class="auth-page">

    <!-- Left branding panel -->
    <div class="auth-brand">
      <div class="auth-brand__inner">
        <router-link to="/" class="auth-logo">
          <img v-if="company && company.logo" :src="imgUrl + 'logo/' + company.logo" alt="Structurecity" />
          <span v-else class="auth-logo__text">Structure<span>city</span></span>
        </router-link>
        <div class="auth-brand__body">
          <h1>Join Africa's Construction &amp; Real Estate Community</h1>
          <p>Create your free account and connect with thousands of professionals across the continent.</p>
          <div class="auth-features">
            <div class="auth-feature">
              <span class="auth-feature__icon">✅</span>
              <span>Free to join — no credit card needed</span>
            </div>
            <div class="auth-feature">
              <span class="auth-feature__icon">📋</span>
              <span>Showcase your portfolio &amp; projects</span>
            </div>
            <div class="auth-feature">
              <span class="auth-feature__icon">🌍</span>
              <span>Connect with professionals in 20+ countries</span>
            </div>
          </div>
        </div>
        <div class="auth-brand__stats">
          <div class="auth-stat"><strong>5,000+</strong><span>Members</span></div>
          <div class="auth-stat"><strong>2,000+</strong><span>Listings</span></div>
          <div class="auth-stat"><strong>20+</strong><span>Countries</span></div>
        </div>
      </div>
    </div>

    <!-- Right form panel -->
    <div class="auth-panel">
      <div class="auth-panel__inner">

        <!-- Mobile logo -->
        <router-link to="/" class="auth-logo auth-logo--mobile">
          <img v-if="company && company.logo" :src="imgUrl + 'logo/' + company.logo" alt="Structurecity" />
          <span v-else class="auth-logo__text">Structure<span>city</span></span>
        </router-link>

        <div class="auth-panel__header">
          <h2>Create your account</h2>
          <p>Join Structurecity for free and start connecting</p>
        </div>

        <div v-if="errorMsg" class="sc-alert sc-alert--error">{{ errorMsg }}</div>
        <div v-if="successMsg" class="sc-alert sc-alert--success">{{ successMsg }}</div>

        <!-- Google sign-up button -->
        <button v-if="!successMsg" type="button" class="btn-google" @click="signInWithGoogle" :disabled="googleLoading">
          <span v-if="googleLoading" class="sc-spinner sc-spinner--dark"></span>
          <template v-else>
            <svg class="btn-google__icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </template>
        </button>

        <!-- Divider -->
        <div v-if="!successMsg" class="auth-divider"><span>or sign up with email</span></div>

        <form v-if="!successMsg" @submit.prevent="register">
          <!-- Name row -->
          <div class="sc-field-row">
            <div class="sc-field">
              <label>First name <span class="req">*</span></label>
              <input type="text" v-model="firstName" placeholder="First name" required />
            </div>
            <div class="sc-field">
              <label>Last name <span class="req">*</span></label>
              <input type="text" v-model="lastName" placeholder="Last name" required />
            </div>
          </div>

          <!-- Email -->
          <div class="sc-field">
            <label>Email address <span class="req">*</span></label>
            <input type="email" v-model="email" placeholder="you@example.com" required autocomplete="email" />
          </div>

          <!-- Phone -->
          <div class="sc-field">
            <label>Phone number</label>
            <div class="sc-phone-row">
              <select name="country_tel_code" v-model="countryCode" class="sc-select sc-select--code">
                <option value="213">+213</option><option value="244">+244</option>
                <option value="1">+1</option><option value="55">+55</option>
                <option value="1">+1 (CA)</option><option value="86">+86</option>
                <option value="20">+20</option><option value="251">+251</option>
                <option value="33">+33</option><option value="49">+49</option>
                <option value="233">+233</option><option value="91">+91</option>
                <option value="62">+62</option><option value="39">+39</option>
                <option value="81">+81</option><option value="254">+254</option>
                <option value="60">+60</option><option value="52">+52</option>
                <option value="212">+212</option><option value="258">+258</option>
                <option value="234" selected>+234 (NG)</option>
                <option value="27">+27</option><option value="249">+249</option>
                <option value="255">+255</option><option value="256">+256</option>
                <option value="44">+44</option><option value="380">+380</option>
                <option value="971">+971</option><option value="598">+598</option>
                <option value="58">+58</option><option value="84">+84</option>
                <option value="260">+260</option><option value="263">+263</option>
              </select>
              <input
                type="tel" v-model="phone"
                name="phone_number"
                placeholder="080 000 0000"
                class="sc-phone-input"
                inputmode="numeric"
                pattern="[0-9+\s\-]*"
                maxlength="15"
              />
            </div>
          </div>

          <!-- Professional? -->
          <div class="sc-field">
            <label>Are you a construction professional?</label>
            <div class="sc-radios">
              <label class="sc-radio">
                <input type="radio" name="is_professional" value="yes" v-model="isProfessional" />
                <span>Yes</span>
              </label>
              <label class="sc-radio">
                <input type="radio" name="is_professional" value="no" v-model="isProfessional" />
                <span>No</span>
              </label>
            </div>
          </div>

          <!-- Profession select -->
          <div v-if="isProfessional === 'yes'" class="sc-field">
            <label>Your profession</label>
            <select v-model="profession" name="profession" class="sc-select">
              <option value="" disabled>Select profession…</option>
              <option v-for="p in professions" :key="p.name" :value="p.name">{{ p.name }}</option>
              <option value="others">Other</option>
            </select>
          </div>

          <!-- Other profession -->
          <div v-if="isProfessional === 'yes' && profession === 'others'" class="sc-field">
            <label>Specify your profession</label>
            <input type="text" v-model="otherProfession" name="other_profession" placeholder="Your profession" />
          </div>

          <!-- Password row -->
          <div class="sc-field-row">
            <div class="sc-field">
              <label>Password <span class="req">*</span></label>
              <div class="sc-field__pw">
                <input :type="showPwd ? 'text' : 'password'" v-model="password" placeholder="Min 8 characters" required minlength="8" autocomplete="new-password" />
                <button type="button" class="sc-field__eye" @click="showPwd = !showPwd" tabindex="-1">
                  <svg v-if="!showPwd" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </div>
            <div class="sc-field">
              <label>Confirm password <span class="req">*</span></label>
              <input :type="showPwd ? 'text' : 'password'" v-model="confirmPassword" placeholder="Repeat password" required autocomplete="new-password" />
            </div>
          </div>

          <!-- Terms -->
          <p class="auth-terms">
            By signing up you agree to our
            <router-link to="/terms-and-conditions" target="_blank" class="auth-link">Terms of Use</router-link> and
            <router-link to="/privacy-policy" target="_blank" class="auth-link">Privacy Policy</router-link>.
          </p>

          <button type="submit" class="auth-btn" :disabled="loading">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Agree &amp; Join</span>
          </button>
        </form>

        <p class="auth-switch">
          Already have an account?
          <router-link to="/sign-in" class="auth-link auth-link--bold">Sign in</router-link>
        </p>

        <p class="auth-back"><router-link to="/" class="auth-link">← Back to home</router-link></p>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Register',

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      company: null,
      firstName: '', lastName: '', email: '',
      countryCode: '234', phone: '',
      isProfessional: '', profession: '', otherProfession: '',
      password: '', confirmPassword: '',
      showPwd: false,
      professions: [],
      loading: false,
      googleLoading: false,
      errorMsg: '',
      successMsg: '',
      tokenClient: null,
    };
  },

  watch: {
    $route: { immediate: true, handler(to) { document.title = to.meta && to.meta.title ? to.meta.title : 'Register — Structurecity'; } },
  },

  created() {
    axios(process.env.VUE_APP_API_URL + 'company')
      .then(res => { if (res && res.data) this.company = res.data; })
      .catch(() => {});
    axios(process.env.VUE_APP_API_URL + 'professions')
      .then(res => { if (res.status === 200) this.professions = res.data || []; })
      .catch(() => {});
  },

  mounted() {
    this.loadGoogleScript();
  },

  methods: {
    loadGoogleScript() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;
      if (!clientId) return;

      if (window.google && window.google.accounts) {
        this.initTokenClient();
        return;
      }

      if (document.getElementById('gsi-script')) {
        window.addEventListener('google-loaded', () => this.initTokenClient(), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = 'gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.dispatchEvent(new Event('google-loaded'));
        this.initTokenClient();
      };
      document.head.appendChild(script);
    },

    initTokenClient() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;
      if (!clientId || !window.google || !window.google.accounts) return;
      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'openid email profile',
        callback: (tokenResponse) => {
          if (tokenResponse.error) {
            this.errorMsg = 'Google sign-in was cancelled or failed.';
            this.googleLoading = false;
            return;
          }
          this.sendGoogleToken(tokenResponse.access_token);
        },
      });
    },

    signInWithGoogle() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;
      if (!clientId) {
        this.errorMsg = 'Google sign-in is not configured.';
        return;
      }
      if (!this.tokenClient) {
        this.errorMsg = 'Google is still loading. Please try again in a moment.';
        return;
      }
      this.errorMsg = '';
      this.googleLoading = true;
      this.tokenClient.requestAccessToken({ prompt: 'select_account' });
    },

    sendGoogleToken(accessToken) {
      this.googleLoading = true;
      this.errorMsg = '';
      axios.post(process.env.VUE_APP_API_URL + 'auth/google', { access_token: accessToken })
        .then(res => {
          if (res.status === 200) {
            localStorage.setItem('user',  res.data.data.id);
            localStorage.setItem('slug',  res.data.data.slug);
            localStorage.setItem('token', res.data.token);
            this.$router.push({ name: 'Home' });
          } else {
            this.errorMsg = 'Google sign-in failed. Please try again.';
          }
        })
        .catch(() => { this.errorMsg = 'Google sign-in failed. Please try again.'; })
        .finally(() => { this.googleLoading = false; });
    },

    register() {
      if (this.password !== this.confirmPassword) {
        this.errorMsg = 'Passwords do not match.';
        return;
      }
      this.loading = true;
      this.errorMsg = '';
      const fd = new FormData();
      fd.append('first_name',     this.firstName);
      fd.append('last_name',      this.lastName);
      fd.append('email',          this.email);
      fd.append('country_tel_code', this.countryCode);
      fd.append('phone_number',   this.phone);
      fd.append('is_professional', this.isProfessional);
      fd.append('profession',     this.profession);
      fd.append('other_profession', this.otherProfession);
      fd.append('password',       this.password);
      fd.append('confirm_password', this.confirmPassword);
      axios.post(process.env.VUE_APP_API_URL + 'auth/register', fd)
        .then(res => {
          if (res.status === 200) {
            this.successMsg = 'Account created! Please check your email to verify your account, then sign in.';
          } else {
            this.errorMsg = res.message || 'Registration failed. Please try again.';
          }
        })
        .catch(() => { this.errorMsg = 'Registration failed. Please check your details and try again.'; })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.auth-page {
  display: grid; grid-template-columns: 1fr 1fr;
  min-height: 100vh;
}

/* ── Left branding panel ── */
.auth-brand {
  background: linear-gradient(145deg, #0a1628 0%, #1e3a5f 50%, #1a4a7a 100%);
  padding: 3rem; display: flex; align-items: center; justify-content: center;
  position: relative; overflow: hidden;
}
.auth-brand::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(circle, rgba(255,255,255,.05) 1px, transparent 1px);
  background-size: 36px 36px;
}
.auth-brand__inner {
  position: relative; z-index: 1; max-width: 420px; width: 100%;
  display: flex; flex-direction: column; gap: 2.5rem;
}

.auth-logo { display: flex; align-items: center; text-decoration: none; }
.auth-logo img { height: 42px; object-fit: contain; }
.auth-logo__text { font-size: 1.75rem; font-weight: 800; color: #fff; letter-spacing: -.03em; }
.auth-logo__text span { color: #f59e0b; }
.auth-logo--mobile { display: none; }

.auth-brand__body { display: flex; flex-direction: column; gap: 1.25rem; }
.auth-brand__body h1 {
  font-size: clamp(1.5rem, 2.5vw, 2rem); font-weight: 800; color: #fff;
  line-height: 1.2; letter-spacing: -.03em; margin: 0;
}
.auth-brand__body p { color: rgba(255,255,255,.65); font-size: 1rem; line-height: 1.65; margin: 0; }

.auth-features { display: flex; flex-direction: column; gap: .875rem; margin-top: .5rem; }
.auth-feature {
  display: flex; align-items: center; gap: .875rem;
  background: rgba(255,255,255,.07); border: 1px solid rgba(255,255,255,.1);
  border-radius: 12px; padding: .875rem 1.125rem;
}
.auth-feature__icon { font-size: 1.25rem; flex-shrink: 0; }
.auth-feature span:last-child { color: rgba(255,255,255,.85); font-size: .9375rem; font-weight: 500; }

.auth-brand__stats {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 1px; background: rgba(255,255,255,.12);
  border-radius: 14px; overflow: hidden;
}
.auth-stat {
  background: rgba(255,255,255,.06); padding: 1.125rem 1rem; text-align: center;
  display: flex; flex-direction: column; gap: .25rem;
}
.auth-stat strong { font-size: 1.5rem; font-weight: 800; color: #fff; letter-spacing: -.03em; line-height: 1; }
.auth-stat span { font-size: .8125rem; color: rgba(255,255,255,.5); font-weight: 500; }

/* ── Right form panel ── */
.auth-panel {
  background: #fff; display: flex; align-items: flex-start; justify-content: center;
  padding: 3rem 2rem; overflow-y: auto;
}
.auth-panel__inner {
  width: 100%; max-width: 480px; display: flex; flex-direction: column; gap: 1.25rem;
  padding: 1rem 0 2rem;
}

.auth-panel__header h2 {
  font-size: 1.875rem; font-weight: 800; color: #0f172a;
  margin: 0 0 .375rem; letter-spacing: -.03em;
}
.auth-panel__header p { color: #64748b; font-size: 1rem; margin: 0; }

/* ── Google button ── */
.btn-google {
  width: 100%; padding: .75rem 1.25rem;
  display: flex; align-items: center; justify-content: center; gap: .75rem;
  background: #fff; color: #3c4043;
  border: 1.5px solid #dadce0; border-radius: 10px;
  font-family: inherit; font-size: .9375rem; font-weight: 600;
  cursor: pointer; transition: background .15s, box-shadow .15s, border-color .15s;
}
.btn-google:hover:not(:disabled) {
  background: #f8fafc; box-shadow: 0 2px 8px rgba(0,0,0,.1); border-color: #c5c8cc;
}
.btn-google:active:not(:disabled) { background: #f1f3f4; }
.btn-google:disabled { opacity: .6; cursor: not-allowed; }
.btn-google__icon { width: 20px; height: 20px; flex-shrink: 0; }

/* ── Divider ── */
.auth-divider {
  display: flex; align-items: center; gap: .875rem;
  color: #94a3b8; font-size: .8125rem;
}
.auth-divider::before,
.auth-divider::after { content: ''; flex: 1; height: 1px; background: #e2e8f0; }

/* ── Fields ── */
.sc-field { display: flex; flex-direction: column; gap: .375rem; margin-bottom: .875rem; }
.sc-field label { font-size: .875rem; font-weight: 600; color: #334155; }
.sc-field input, .sc-select {
  width: 100%; padding: .6875rem 1rem;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  font-family: inherit; font-size: .9375rem; color: #0f172a;
  background: #fff; outline: none;
  transition: border-color .15s, box-shadow .15s;
  -webkit-appearance: none; appearance: none;
}
.sc-field input:focus, .sc-select:focus { border-color: #1e3a5f; box-shadow: 0 0 0 3px rgba(30,58,95,.1); }

.sc-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: .75rem; }

.sc-phone-row { display: flex; gap: .5rem; }
.sc-select--code { width: 110px; flex-shrink: 0; padding-right: .5rem; }
.sc-phone-input {
  flex: 1; padding: .6875rem 1rem;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  font-family: inherit; font-size: .9375rem; color: #0f172a;
  background: #fff; outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.sc-phone-input:focus { border-color: #1e3a5f; box-shadow: 0 0 0 3px rgba(30,58,95,.1); }

.sc-field__pw { position: relative; }
.sc-field__pw input { padding-right: 2.5rem; }
.sc-field__eye {
  position: absolute; right: .75rem; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: #94a3b8; padding: 0;
  display: flex; align-items: center; transition: color .15s;
}
.sc-field__eye:hover { color: #1e3a5f; }

.sc-radios { display: flex; gap: 1.5rem; padding-top: .25rem; }
.sc-radio {
  display: flex; align-items: center; gap: .5rem;
  font-size: .9rem; color: #334155; cursor: pointer;
}
.sc-radio input[type="radio"] { accent-color: #1e3a5f; width: 16px; height: 16px; margin: 0; }

.req { color: #dc2626; }

.auth-terms { font-size: .8125rem; color: #64748b; margin: 0; line-height: 1.5; }
.auth-link { color: #1e3a5f; text-decoration: none; font-size: .875rem; font-weight: 500; }
.auth-link:hover { text-decoration: underline; }
.auth-link--bold { font-weight: 700; }

.auth-btn {
  width: 100%; padding: .8125rem 1.5rem;
  background: #1e3a5f; color: #fff; border: none; border-radius: 10px;
  font-family: inherit; font-size: 1rem; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: .5rem;
  transition: background .15s, transform .1s, box-shadow .15s;
}
.auth-btn:hover:not(:disabled) { background: #2a4f7c; box-shadow: 0 4px 16px rgba(30,58,95,.3); }
.auth-btn:active:not(:disabled) { transform: translateY(1px); }
.auth-btn:disabled { opacity: .6; cursor: not-allowed; }

.auth-switch { text-align: center; color: #64748b; font-size: .9rem; margin: 0; }
.auth-back { text-align: center; margin: 0; }
.auth-back .auth-link { color: #94a3b8; }

/* ── Alerts ── */
.sc-alert { padding: .75rem 1rem; border-radius: 8px; font-size: .875rem; }
.sc-alert--error   { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
.sc-alert--success { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }

/* ── Spinner ── */
.sc-spinner {
  display: inline-block; width: 18px; height: 18px;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  border-radius: 50%; animation: spin .7s linear infinite;
}
.sc-spinner--dark {
  border-color: rgba(0,0,0,.15); border-top-color: #3c4043;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Responsive ── */
@media (max-width: 900px) {
  .auth-page { grid-template-columns: 1fr; }
  .auth-brand { display: none; }
  .auth-logo--mobile { display: flex; }
  .auth-panel { padding: 2.5rem 1.5rem; min-height: 100vh; }
  .sc-field-row { grid-template-columns: 1fr; }
}
</style>
